import { getMessaging, getToken, onMessage, Messaging } from 'firebase/messaging';
import { doc, updateDoc } from 'firebase/firestore';
import { db, firebaseConfig } from '../firebase/firebaseConfig';
import { Capacitor } from '@capacitor/core';

class FCMService {
  private messaging: Messaging | null = null;
  private vapidKey = "BCJJBcUeb-QvrmWxa3yku9SYKFgnugpazKb7cuNJzAlAft7wci8B8zG3RlbZzsqCBIXVnDuC3Xjs7JcftrXq9c4"; // Reemplazar con tu VAPID key de Firebase Console

  /**
   * Inicializar Firebase Cloud Messaging
   */
  async initialize(): Promise<void> {
    try {
      // En apps nativas (Capacitor) no usamos Firebase Messaging web + Service Worker.
      // Evita que un SW persista entre updates y genere errores de chunks con hash.
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'android' || platform === 'ios';
      if (isNative) {
        return;
      }

      // Verificar si el navegador soporta notificaciones
      if (!('Notification' in window)) {
        return;
      }

      // Verificar si el service worker está disponible
      if (!('serviceWorker' in navigator)) {
        return;
      }

      // Reutilizar registro existente de firebase-messaging-sw.js si ya existe
      let existingRegistration: ServiceWorkerRegistration | undefined;
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        existingRegistration = registrations.find(r => 
          r.active?.scriptURL.includes('firebase-messaging-sw.js') ||
          r.installing?.scriptURL.includes('firebase-messaging-sw.js') ||
          r.waiting?.scriptURL.includes('firebase-messaging-sw.js')
        );
      } catch (e) {
        // Error silently ignored
      }

      let registration: ServiceWorkerRegistration;
      if (existingRegistration) {
        registration = existingRegistration;
      } else {
        // Construir query string con credenciales de Firebase para inicializar dinámicamente el SW
        const configParams = new URLSearchParams({
          apiKey: firebaseConfig.apiKey || '',
          authDomain: firebaseConfig.authDomain || '',
          projectId: firebaseConfig.projectId || '',
          storageBucket: firebaseConfig.storageBucket || '',
          messagingSenderId: firebaseConfig.messagingSenderId || '',
          appId: firebaseConfig.appId || '',
          measurementId: firebaseConfig.measurementId || '',
        }).toString();

        // Registrar el service worker con timeout
        const registrationPromise = navigator.serviceWorker.register(`/firebase-messaging-sw.js?${configParams}`, {
          updateViaCache: 'none'
        });
        
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Service worker registration timeout')), 10000)
        );

        registration = (await Promise.race([registrationPromise, timeoutPromise])) as ServiceWorkerRegistration;
      }

      // Inicializar messaging
      this.messaging = getMessaging();

      // Escuchar mensajes en primer plano
      this.listenToForegroundMessages();
    } catch (error) {
      console.warn('FCM initialization failed (non-critical):', error);
      // No hacer nada más - la app debe funcionar sin FCM
    }
  }

  /**
   * Solicitar permiso para notificaciones y obtener token
   */
  async requestPermissionAndGetToken(userId: string): Promise<string | null> {
    try {
      // Verificar si Notification API está disponible
      if (!('Notification' in window)) {
        return null;
      }

      // Solicitar permiso
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        return null;
      }

      if (!this.messaging) {
        await this.initialize();
      }

      if (!this.messaging) {
        return null;
      }

      // Obtener token FCM con timeout
      const tokenPromise = getToken(this.messaging, {
        vapidKey: this.vapidKey,
      });
      
      const timeoutPromise = new Promise<string>((_, reject) => 
        setTimeout(() => reject(new Error('getToken timeout')), 15000)
      );

      const token = await Promise.race([tokenPromise, timeoutPromise]);

      if (token) {
        
        // Guardar token en Firestore
        await this.saveTokenToFirestore(userId, token);
        
        return token;
      } else {
        return null;
      }
    } catch (error) {
      console.warn('Error requesting FCM token:', error);
      return null;
    }
  }

  /**
   * Guardar token en Firestore
   */
  private async saveTokenToFirestore(userId: string, token: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'users', userId), {
        fcmToken: token,
        fcmTokenUpdatedAt: new Date(),
      });
    } catch (error) {
    }
  }

  /**
   * Escuchar mensajes en primer plano
   */
  private listenToForegroundMessages(): void {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {
      console.log('📬 Push notification recibida en foreground:', payload);

      // Usar service worker registration para mostrar notificación
      // (Más confiable que new Notification())
      if ('serviceWorker' in navigator && 'Notification' in window && Notification.permission === 'granted') {
        navigator.serviceWorker.ready.then((registration) => {
          const notificationTitle = payload.notification?.title || 'Nueva notificación';
          const notificationOptions = {
            body: payload.notification?.body || '',
            icon: payload.notification?.icon || '/images/logo/logo-icon.svg',
            badge: '/images/logo/logo-icon.svg',
            tag: payload.data?.notificationId || 'default',
            data: payload.data,
            requireInteraction: false,
            vibrate: [200, 100, 200],
            // Asegurar que se muestre como notificación del sistema
            silent: false,
          };

          registration.showNotification(notificationTitle, notificationOptions);
          console.log('✅ Notificación mostrada via Service Worker');
        }).catch((error) => {
          console.error('Error showing notification via SW:', error);
          
          // Fallback a notificación directa
          const notificationTitle = payload.notification?.title || 'Nueva notificación';
          const notificationOptions = {
            body: payload.notification?.body || '',
            icon: payload.notification?.icon || '/images/logo/logo-icon.svg',
            badge: '/images/logo/logo-icon.svg',
            tag: payload.data?.notificationId || 'default',
            data: payload.data,
            requireInteraction: false,
          };

          const notification = new Notification(notificationTitle, notificationOptions);
          
          notification.onclick = () => {
            window.focus();
            const link = payload.data?.link || '/notifications';
            import('../utils/navigation').then(({ navigateTo }) => {
              navigateTo(link);
            });
            notification.close();
          };
        });
      }
    });
  }

  /**
   * Verificar si el usuario tiene permisos de notificación
   */
  hasPermission(): boolean {
    if (!('Notification' in window)) {
      return false;
    }
    return Notification.permission === 'granted';
  }

  /**
   * Verificar estado del permiso
   */
  getPermissionStatus(): NotificationPermission | null {
    if (!('Notification' in window)) {
      return null;
    }
    return Notification.permission;
  }
}

// Instancia singleton
const fcmService = new FCMService();

export default fcmService;
